import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './cart-item-summary.scss';
import { Icon } from '@ampol/design-system';
import CardImage from '../../../../components/card-image/card-image';

/**
 * A cart item summary component
 * @param cartItem The cart item object
 * @param inlineView Wider view, recipients list is inline, quantity is a different display and delivery message is normal font
 * @param showImage show the card image
 * @param showTotal show the total cost
 * @param showQuantity show the card count selected
 * @param showDelivery show the delivery message
 * @param showRecipients show the Recipients list and Add Recipient button (conditionally)
 * @param recipientsAddClick parent event
 * @param recipientsEditClick parent event
 * @param recipientsRemoveClick parent event
 * @returns JSX Element
 */

const CartItemSummary = ( { cartItem, inlineView, showImage, showTotal, showQuantity, showDelivery, showRecipients, recipientsAddClick, recipientsEditClick, recipientsRemoveClick }) => {
  const physicalAddressRecipient = useSelector((state) => state.cashStore.cashOrderPhysicalAddressRecipient);
  const deliveryOption = useSelector((state) => state.cashStore.cashOrderDeliveryOption);

  const [recipients, setRecipients] = useState([]);
  const [remainingRecipients, setRemainingRecipients] = useState(0);

  const { mediaType, denomination, denominationCode, quantity, total, isFuelOnly, verificationType } = cartItem || {};
  const cost = Number(denominationCode) === 0 ? Number(denomination) : Number(denominationCode);

  useEffect(() => {
    if (cartItem?.mediaType === 'P') {
      if (physicalAddressRecipient) {
        setRecipients([{
          ...physicalAddressRecipient,
          quantity: cartItem.quantity
        }]);
        setRemainingRecipients(0);
      } else {
        setRecipients([]);
        setRemainingRecipients(cartItem?.quantity);
      }
    } else {
      setRecipients(cartItem?.recipients);
      setRemainingRecipients(cartItem?.remainingRecipients);
    }
  }, [cartItem, physicalAddressRecipient]);

  const showDeliveryMessage = () => {
    if (deliveryOption === 'optionThree' && (!recipients || recipients?.length === 0)) {
      return '';
    }
    else {
      return `${quantity > 1 ? 'These cards' : 'This card'} will be sent to ${mediaType === 'P' || recipients?.length <= 1 ? 'one address' : 'different addresses'}`;
    }
  };

  return <>
    <div className={'cart-item'}>
      {(showImage || showTotal) && <div className='cart-item__header'>
        {showImage && <CardImage useCacheImage={true} isLarge={false} isDigital={mediaType === 'D'} imageSource={cartItem?.plasticTypeSource}/>}
        {showTotal && <span className='cart-item__total-cost'>${total}</span>}
      </div>}

      <div className='cart-item__main'>
        <div>
          <h1 className='cart-item__title'>AmpolCash ${cost}</h1>
          <span className='cart-item__content'>{mediaType === 'P' ? 'Physical' : 'Digital'}{isFuelOnly && ' | Fuel only'}
            {verificationType === 'P' && ' | PIN required'}</span>
        </div>

        {showQuantity && (inlineView ? <div className='cart-item__bold'>Quantity: {quantity}</div> : <div className='cart-item__content'>({quantity} card{quantity > 1 ? 's' : ''})</div>)}

        {showRecipients && <div className={inlineView ? 'cart-item__recipients-inline' : ''}>
          {inlineView && recipients?.length > 0 && <span className='cart-item__bold'>Recipients: </span>}
          <ul className={`cart-item__recipients-list ${inlineView ? 'cart-item__recipients-list--inline' : ''}`} aria-label={'recipients'}>
            {recipientsEditClick && recipients && recipients.map((item) => <li key={item.uuid}>
              <button onClick={() => recipientsEditClick(cartItem.uuid, item)} className='cart-item__recipient'>
                {(item.firstName || item.lastName) && <span className='cart-item__recipient-full-name'>[<span className='cart-item__recipient-name'>{item.firstName} {item.lastName}</span>]</span> }
                <span>({item.quantity} card{item.quantity > 1 ? 's' : ''})</span>
              </button>
              <button onClick={() => recipientsRemoveClick(cartItem.uuid, item, cartItem.mediaType === 'P')} className='cart-item__remove' >
                <Icon url={'/icons/close.svg'} size={24} className='cart-item__remove-icon' aria-hidden='true'/>
                <span className='cart-item__visually-hidden'>Remove recipient</span>
              </button>
            </li>)}
          </ul>
          {(recipientsAddClick && !!remainingRecipients) && <div>
            <button onClick={() => recipientsAddClick(cartItem.uuid)} className='cart-item__add-recipients' >
              Add recipient{mediaType === 'D' && '(s)'}
            </button>
          </div>}
        </div>}

        {showDelivery &&
          <div className={`cart-item__deliveryMsg ${inlineView && 'cart-item__deliveryMsg--inline-view'}`}>
            {showDeliveryMessage()}
          </div>}
      </div>
    </div>
  </>;

};

export default CartItemSummary;
